import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/firebase';

const RevenueTracker = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalGames: 0,
    marketplaceGames: 0,
    totalSales: 0,
    totalRevenue: 0,
    potentialRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRevenueStats = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Load user's created games (personal)
      const createdGamesSnapshot = await getDocs(
        query(collection(db, 'games'), where('ownerId', '==', user.uid))
      );
      const createdGames = createdGamesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Load user's marketplace games from canonical path
      const marketplaceByCreatorSnap = await getDocs(
        query(collection(db, 'games', 'content', 'triviaGames'), where('creatorId', '==', user.uid))
      );
      const marketplaceByOwnerSnap = await getDocs(
        query(collection(db, 'games', 'content', 'triviaGames'), where('ownerId', '==', user.uid))
      );
      const marketplaceCombinedMap = new Map();
      marketplaceByCreatorSnap.docs.forEach(d => marketplaceCombinedMap.set(d.id, { id: d.id, ...d.data() }));
      marketplaceByOwnerSnap.docs.forEach(d => marketplaceCombinedMap.set(d.id, { id: d.id, ...d.data() }));
      const marketplaceGamesAll = Array.from(marketplaceCombinedMap.values());

      // Keep only approved/public marketplace games for sales/revenue
      const marketplaceGames = marketplaceGamesAll.filter(game => 
        game.marketplaceData &&
        (game.marketplaceData.approvalStatus === 'approved' || game.status === 'approved' || game.status === 'public')
      );

      const totalSales = marketplaceGames.reduce((sum, game) => 
        sum + (game.marketplaceData?.salesCount || 0), 0
      );

      // creatorRevenue is stored in cents; convert to dollars for display
      const totalRevenue = marketplaceGames.reduce((sum, game) => {
        const cents = game.marketplaceData?.creatorRevenue || 0;
        return sum + (cents / 100);
      }, 0);

      // Estimate potential revenue (assuming each marketplace game could sell 10 times)
      const potentialRevenue = marketplaceGames.reduce((sum, game) => {
        const gameListingPrice = game.marketplaceData?.price || 20; // $20 minimum game listing price
        const launchFee = 15; // $15 launch fee
        const totalCheckout = gameListingPrice + launchFee; // Total at checkout
        const potentialSales = 10; // Conservative estimate
        const creatorShare = gameListingPrice * 0.5; // 50% of game listing price
        return sum + (creatorShare * potentialSales); // Creator revenue per sale
      }, 0);

      setStats({
        totalGames: createdGames.length,
        marketplaceGames: marketplaceGames.length,
        totalSales,
        totalRevenue,
        potentialRevenue
      });
    } catch (error) {
      console.error('Error loading revenue stats:', error);
      // Set user-friendly error message
      if (error.code === 'permission-denied') {
        setError('Unable to load revenue data. You may not have permission to view this information.');
      } else if (error.code === 'unavailable') {
        setError('Unable to connect to the server. Please check your internet connection.');
      } else {
        setError('Failed to load revenue statistics. Please try again later.');
      }
      // Set default stats if we can't load them - this is normal for new users
      setStats({
        totalGames: 0,
        marketplaceGames: 0,
        totalSales: 0,
        totalRevenue: 0,
        potentialRevenue: 0
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadRevenueStats();
  }, [loadRevenueStats]);

  const handleCreateGame = () => {
    navigate('/add-game');
  };

  const handleViewMarketplace = () => {
    navigate('/marketplace');
  };

  const handleViewCreatorDashboard = () => {
    navigate('/my-games');
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-700 rounded mb-4"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-16 bg-gray-700 rounded"></div>
            <div className="h-16 bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-red-300 font-semibold mb-2">Unable to Load Revenue Data</h3>
              <p className="text-red-200 text-sm mb-3">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  loadRevenueStats();
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-bold text-white">💰 Revenue Tracker</h3>
        <button
          onClick={handleViewCreatorDashboard}
          className="text-blue-400 hover:text-blue-300 text-sm"
        >
          Manage Games →
        </button>
      </div>

      {/* Current Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-400">{stats.totalGames}</div>
          <div className="text-gray-400 text-sm">Total Games</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-400">{stats.marketplaceGames}</div>
          <div className="text-gray-400 text-sm">Marketplace</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-400">{stats.totalSales}</div>
          <div className="text-gray-400 text-sm">Total Sales</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-yellow-400">${stats.totalRevenue.toFixed(2)}</div>
          <div className="text-gray-400 text-sm">Earned</div>
        </div>
      </div>

      {/* Revenue Potential */}
      {stats.marketplaceGames > 0 && (
        <div className="bg-green-900/20 border border-green-600 rounded-lg p-4 mb-6">
          <h4 className="text-green-400 font-semibold mb-2">🎯 Revenue Potential</h4>
          <div className="flex justify-between items-center">
            <div>
              <div className="text-white font-bold text-lg">${stats.potentialRevenue.toFixed(0)}</div>
              <div className="text-gray-300 text-sm">Potential earnings from current games</div>
            </div>
            <div className="text-right">
              <div className="text-green-400 font-semibold">
                {((stats.totalRevenue / stats.potentialRevenue) * 100).toFixed(1)}%
              </div>
              <div className="text-gray-400 text-sm">of potential reached</div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        {stats.marketplaceGames === 0 && (
          <div className="bg-blue-900/20 border border-blue-600 rounded-lg p-4 mb-4">
            <h4 className="text-blue-400 font-semibold mb-2">🚀 Start Earning Money</h4>
            <p className="text-gray-300 text-sm mb-3">
              Create games and submit them to the marketplace to start earning passive income. 
              You'll get 50% of the game price ($10 per $35 game sold - $20 game price + $15 launch fee)!
            </p>
            <button
              onClick={handleCreateGame}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Create Your First Game
            </button>
          </div>
        )}

        {stats.marketplaceGames > 0 && stats.totalSales === 0 && (
          <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4 mb-4">
            <h4 className="text-yellow-400 font-semibold mb-2">📈 Promote Your Games</h4>
            <p className="text-gray-300 text-sm mb-3">
              You have {stats.marketplaceGames} games in the marketplace but no sales yet. 
              Consider creating more games or improving existing ones.
            </p>
            <div className="space-x-2">
              <button
                onClick={handleCreateGame}
                className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                Create More Games
              </button>
              <button
                onClick={handleViewMarketplace}
                className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                View Marketplace
              </button>
            </div>
          </div>
        )}

        {stats.totalSales > 0 && (
          <div className="bg-green-900/20 border border-green-600 rounded-lg p-4 mb-4">
            <h4 className="text-green-400 font-semibold mb-2">🎉 You're Earning!</h4>
            <p className="text-gray-300 text-sm mb-3">
              Great job! You've earned ${stats.totalRevenue} from {stats.totalSales} sales. 
              Keep creating quality games to increase your earnings.
            </p>
            <div className="space-x-2">
              <button
                onClick={handleCreateGame}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                Create More Games
              </button>
              <button
                onClick={handleViewCreatorDashboard}
                className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                View Analytics
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-center space-x-2">
          <button
            onClick={handleCreateGame}
            className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            Create Game
          </button>
          <button
            onClick={handleViewMarketplace}
            className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            Browse Marketplace
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevenueTracker; 